<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';

import {
  sumFunnelValues,
  formatFunnelValue,
} from 'dashboard/helper/funnelHelper';
import FunnelCard from 'dashboard/components-next/funnels/FunnelCard.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  cards: {
    type: Array,
    default: () => [],
  },
  // Archived view: cards are shown in their stage for context, but dragging is
  // off — reordering a card nobody can see has no meaning, and a drop would
  // silently move a card that is not on the board.
  archived: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['cardMove']);

const { t } = useI18n();

const totalValue = computed(() =>
  formatFunnelValue(sumFunnelValues(props.cards))
);

// vuedraggable emits { added | moved | removed }. We only act on the
// destination column (added = dropped from another column, moved = reordered
// within this column) and report the card, target stage and new index.
const onChange = event => {
  const change = event.added || event.moved;
  if (!change) return;

  emit('cardMove', {
    stageId: props.stage.id,
    cardId: change.element.id,
    position: change.newIndex,
  });
};
</script>

<template>
  <div
    class="flex flex-col w-72 shrink-0 h-full rounded-lg bg-n-solid-1 border border-n-weak"
  >
    <header
      class="flex items-center gap-2 px-3 py-2.5 border-b border-n-weak shrink-0"
    >
      <span
        v-if="stage.color"
        class="size-2.5 rounded-full shrink-0"
        :style="{ backgroundColor: stage.color }"
      />
      <h2 class="text-sm font-medium truncate text-n-slate-12">
        {{ stage.name }}
      </h2>
      <span class="text-xs text-n-slate-11 shrink-0">{{ cards.length }}</span>
      <span v-if="totalValue" class="ml-auto text-xs text-n-slate-11 shrink-0">
        {{ totalValue }}
      </span>
    </header>

    <Draggable
      :list="cards"
      :disabled="archived"
      group="funnel-cards"
      item-key="id"
      tag="div"
      ghost-class="opacity-50"
      class="flex flex-col flex-1 gap-2 p-2 overflow-y-auto"
      @change="onChange"
    >
      <template #item="{ element }">
        <FunnelCard :card="element" :archived="archived" />
      </template>
    </Draggable>

    <p
      v-if="!cards.length"
      class="px-2 pb-4 text-xs text-center text-n-slate-10"
    >
      {{ t('FUNNELS.STAGE.EMPTY') }}
    </p>
  </div>
</template>
